import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shrinkImage } from '../utils/shrinkImage';
import { GetToken, scorecardApi } from '../utils/scorecardApi';
import '../css/scorecard.css';

interface ScorecardUploadButtonProps {
  roundId: number;
  getToken: GetToken;
  label?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Called with the new upload id; by default the review page opens. */
  onUploaded?: (uploadId: number) => void;
}

/**
 * "📷 Scorecard": pick or take a photo of the paper card, shrink it in the
 * browser (EXIF rotation respected, ~2400px JPEG), upload it to the round, and
 * open the review page while Claude reads it.
 */
const ScorecardUploadButton: React.FC<ScorecardUploadButtonProps> = ({ roundId, getToken, label = '📷 Scorecard', className = 'sc-btn sc-btn--sm', style, onUploaded }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type && !file.type.startsWith('image/')) { setError('Choose a photo (JPG or PNG).'); return; }
    setBusy(true);
    setError(null);
    try {
      const img = await shrinkImage(file);
      const { upload_id: uploadId } = await scorecardApi.upload(getToken, roundId, img.blob, {
        filename: file.name, width: img.width, height: img.height,
      });
      if (onUploaded) onUploaded(uploadId);
      else navigate(`/scorecard-review/${uploadId}`);
    } catch (err: any) {
      setError(err?.message || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
      <button
        type="button"
        className={className}
        style={style}
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        title="Upload a photo of the paper scorecard to fill in hole-by-hole scores"
      >
        {busy ? 'Uploading…' : label}
      </button>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
      {error && <span className="sc-error" role="alert">{error}</span>}
    </span>
  );
};

export default ScorecardUploadButton;
